/**
 * Purpose: Shared face-image payload limits for API routes and capture UI.
 * Inputs: Base64 strings from client capture/upload paths; Vision Eval action payloads.
 * Outputs: Constants and client-safe size checks.
 */

/** Max base64 payload for face routes (~6 MB raw JPEG). */
export const FACE_IMAGE_MAX_BASE64_LEN = 8_000_000

/** Human-readable limit shown in capture UI copy. */
export const FACE_IMAGE_MAX_SIZE_LABEL = '6 MB'

/**
 * Total Server Action body ceiling for Vision Eval. Must stay in sync with
 * `experimental.serverActions.bodySizeLimit` in `next.config.ts` — Next rejects
 * the request before Zod runs when this is exceeded.
 */
export const VISION_EVAL_ACTION_BODY_LIMIT_BYTES = 50 * 1024 * 1024

/** Human-readable Vision Eval body limit for admin UI copy. */
export const VISION_EVAL_ACTION_BODY_LIMIT_LABEL = '50 MB'

export function isFaceImageBase64WithinLimit(base64: string): boolean {
  return base64.length <= FACE_IMAGE_MAX_BASE64_LEN
}

/** Serialized JSON byte length of any Vision Eval action payload. */
export function estimateVisionEvalPayloadBytes(payload: unknown): number {
  return JSON.stringify(payload).length
}

export function isVisionEvalPayloadWithinLimit(payload: unknown): boolean {
  return estimateVisionEvalPayloadBytes(payload) <= VISION_EVAL_ACTION_BODY_LIMIT_BYTES
}

/** Formats a byte count as `X.X MB` for the Vision Eval size meter. */
export function formatPayloadMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
